import api from './api';

export interface StatisticItem {
  value: number;
  change: number;
  previous_value: number;
}

export interface DashboardStatistics {
  revenue: StatisticItem;
  orders: StatisticItem;
  avg_order_value: StatisticItem;
  new_customers: StatisticItem;
}

export interface StatisticsResponse {
  success: boolean;
  data: DashboardStatistics;
  time_range: string;
  period: {
    start_date: string;
    end_date: string;
  };
}

export interface SalesTrendData {
  date: string;
  services?: Record<string, {
    amount: number;
    count: number;
  }>;
  sellers?: Record<string, {
    amount: number;
    profit: number;
    count: number;
  }>;
}

export interface SalesTrendResponse {
  success: boolean;
  data: {
    chart_data: SalesTrendData[];
    services?: string[];
    sellers?: string[];
    group_by: 'service' | 'seller';
  };
  time_range: string;
}

export interface BusinessCompositionItem {
  name: string;
  value: number;
  count: number;
  profit: number;
  percentage: number;
}

export interface BusinessCompositionResponse {
  success: boolean;
  data: {
    composition: BusinessCompositionItem[];
    total_amount: number;
    total_orders: number;
  };
  time_range: string;
}

export interface NationDistributionItem {
  nation: string;
  count: number;
  amount: number;
  percentage: number;
}

export interface NationDistributionResponse {
  success: boolean;
  data: {
    distribution: NationDistributionItem[];
    total_orders: number;
  };
  time_range: string;
}

export interface ClassDistributionItem {
  primary_class: string;
  count: number;
  amount: number;
  percentage: number;
}

export interface ClassDistributionResponse {
  success: boolean;
  data: {
    distribution: ClassDistributionItem[];
    total_orders: number;
  };
  time_range: string;
}

export interface NewClientsResponse {
  success: boolean;
  data: {
    chart_data: Array<{ date: string; client_count: number }>;
    total_clients: number;
    client_types?: Array<{ client_type: string; client_count: number }>;
  };
  time_range: string;
}

export interface DashboardParams {
  time_range?: 'week' | 'month' | 'quarter' | 'year';
  group_by?: 'service' | 'seller';
  start_date?: string;
  end_date?: string;
  seller_id?: string | number;
}

// 待办事项相关类型
export interface TodoItemCategory {
  count: number;
  data: any[];
}

export interface TodoItemsResponse {
  success: boolean;
  data: {
    pending_payment: TodoItemCategory;
    pending_submission: TodoItemCategory;
    pending_notice: TodoItemCategory;
    abnormal: TodoItemCategory;
  };
  pagination: {
    category: 'pending_payment' | 'pending_submission' | 'pending_notice' | 'abnormal';
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

class DashboardService {
  // 获取仪表盘统计数据
  async getStatistics(params: DashboardParams = {}): Promise<StatisticsResponse> {
    const response = await api.get('/dashboard/statistics', { params });
    return response.data;
  }

  // 获取销售趋势数据
  async getSalesTrend(params: DashboardParams = {}): Promise<SalesTrendResponse> {
    const response = await api.get('/dashboard/sales-trend', { params });
    return response.data;
  }

  // 获取业务构成分析数据
  async getBusinessComposition(params: DashboardParams = {}): Promise<BusinessCompositionResponse> {
    const response = await api.get('/dashboard/business-composition', { params });
    return response.data;
  }

  // 获取国家分布数据
  async getNationDistribution(params: DashboardParams = {}): Promise<NationDistributionResponse> {
    const response = await api.get('/dashboard/nation-distribution', { params });
    return response.data;
  }

  // 获取类目分布数据
  async getClassDistribution(params: DashboardParams = {}): Promise<ClassDistributionResponse> {
    const response = await api.get('/dashboard/class-distribution', { params });
    return response.data;
  }

  // 获取新增客户统计
  async getNewClients(params: DashboardParams = {}): Promise<NewClientsResponse> {
    const response = await api.get('/dashboard/new-clients', { params });
    return response.data;
  }

  // 获取待办事项数据（从后端过滤）
  async getTodoItems(params: {
    category?: 'pending_payment' | 'pending_submission' | 'pending_notice' | 'abnormal';
    page?: number;
    per_page?: number;
  } = {}): Promise<TodoItemsResponse> {
    const response = await api.get('/dashboard/todo-items', { params });
    return response.data;
  }

  // 获取所有仪表盘数据
  async getAllDashboardData(params: DashboardParams = {}) {
    try {
      const [statistics, salesTrend, businessComposition, nationDistribution, classDistribution] = await Promise.all([
        this.getStatistics(params),
        this.getSalesTrend(params),
        this.getBusinessComposition(params),
        this.getNationDistribution(params),
        this.getClassDistribution(params),
      ]);

      return {
        success: true,
        data: {
          statistics: statistics.data,
          salesTrend: salesTrend.data,
          businessComposition: businessComposition.data,
          nationDistribution: nationDistribution.data,
          classDistribution: classDistribution.data,
          period: statistics.period,
        },
        time_range: params.time_range || 'week',
      };
    } catch (error) {
      console.error('获取仪表盘数据失败:', error);
      throw error;
    }
  }

  // 格式化统计数据为组件需要的格式
  formatStatisticsForComponents(statistics: DashboardStatistics) {
    return [
      {
        title: '毛利润',
        value: statistics.revenue.value,
        change: statistics.revenue.change,
        type: 'revenue' as const,
      },
      {
        title: '订单数量',
        value: statistics.orders.value,
        change: statistics.orders.change,
        type: 'orders' as const,
      },
      {
        title: '平均客单价',
        value: statistics.avg_order_value.value,
        change: statistics.avg_order_value.change,
        type: 'price' as const,
      },
      {
        title: '新增客户数',
        value: statistics.new_customers.value,
        change: statistics.new_customers.change,
        type: 'customers' as const,
      },
    ];
  }

  // 格式化销售趋势数据为图表组件需要的格式，并携带系列名称
  formatSalesTrendForChart(salesTrendData: {
    chart_data: SalesTrendData[],
    services?: string[],
    sellers?: string[],
    group_by: 'service' | 'seller'
  }) {
    const { chart_data, services, sellers, group_by } = salesTrendData;

    const formatted = chart_data.map(item => {
      const result: any = {
        date: this.formatDateForChart(item.date),
      };

      if (group_by === 'seller' && sellers && item.sellers) {
        sellers.forEach(seller => {
          const sellerData = item.sellers?.[seller];
          result[`${seller}_amount`] = sellerData ? sellerData.amount : 0;
          result[`${seller}_profit`] = sellerData ? sellerData.profit : 0;
          result[`${seller}_count`] = sellerData ? sellerData.count : 0;
        });
      } else if (group_by === 'service' && services && item.services) {
        services.forEach(service => {
          const serviceData = item.services?.[service];
          result[`${service}_amount`] = serviceData ? serviceData.amount : 0;
          result[`${service}_count`] = serviceData ? serviceData.count : 0;
        });
      }

      return result;
    });

    const series = group_by === 'seller' ? (sellers || []) : (services || []);

    return {
      data: formatted,
      series,
      groupBy: group_by,
    };
  }

  // 格式化业务构成数据为饼图组件需要的格式
  formatBusinessCompositionForChart(compositionData: { composition: BusinessCompositionItem[] }) {
    const colors = ['#1f1f1f', '#ff6b35', '#4285f4', '#34a853', '#9c27b0', '#ff9800', '#f44336'];

    // 过滤掉数值为0的项目，只显示有实际业务的业务类型
    const filteredComposition = compositionData.composition.filter(item => item.value > 0);

    return filteredComposition.map((item, index) => ({
      name: item.name,
      value: item.percentage, // 使用百分比作为饼图的数值
      color: colors[index % colors.length],
      count: item.count, // 添加订单数量
      profit: item.profit, // 添加利润信息
      amount: item.value, // 添加实际金额
    }));
  }

  // 格式化国家分布数据为饼图组件需要的格式
  formatNationDistributionForChart(distributionData: { distribution: NationDistributionItem[] }) {
    const colors = ['#1f1f1f', '#4f46e5', '#22c55e', '#eab308', '#ef4444', '#06b6d4', '#a855f7', '#f97316'];

    const filtered = (distributionData.distribution || []).filter(item => item.percentage > 0);

    return filtered.map((item, index) => ({
      name: item.nation || '未填写国家',
      value: item.percentage,
      color: colors[index % colors.length],
      count: item.count,
      amount: item.amount,
    }));
  }

  // 格式化类目分布数据为饼图组件需要的格式
  formatClassDistributionForChart(distributionData: { distribution: ClassDistributionItem[] }) {
    const colors = ['#0ea5e9', '#f97316', '#22c55e', '#a855f7', '#e11d48', '#6366f1', '#84cc16', '#14b8a6'];

    const filtered = (distributionData.distribution || []).filter(item => item.percentage > 0);

    return filtered.map((item, index) => ({
      name: item.primary_class || '未填写类目',
      value: item.percentage,
      color: colors[index % colors.length],
      count: item.count,
      amount: item.amount,
    }));
  }

  // 格式化新增客户统计数据
  formatNewClientsForChart(newClientsData: { chart_data: Array<{ date: string; client_count: number }> }) {
    return (newClientsData?.chart_data || []).map(item => ({
      date: item.date,
      client_count: item.client_count,
    }));
  }

  // 格式化客户等级分布
  formatClientTypes(newClientsData: { client_types?: Array<{ client_type: string; client_count: number }> }) {
    return (newClientsData?.client_types || []).map(item => ({
      type: item.client_type,
      count: item.client_count,
    }));
  }

  // 格式化日期显示
  private formatDateForChart(dateStr: string): string {
    const date = new Date(dateStr);

    if (dateStr.includes('-') && dateStr.split('-').length === 2) {
      // 年月格式 (YYYY-MM)
      return `${date.getMonth() + 1}月`;
    } else {
      // 日期格式 (YYYY-MM-DD)
      return `${date.getMonth() + 1}/${date.getDate()}`;
    }
  }
}

const dashboardService = new DashboardService();
export default dashboardService;
